import { useLocation } from '@reach/router';
import { useLiveQuery } from '@sanity/preview-kit';
import { graphql } from 'gatsby';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import { $pageAbTest } from '../stores/page-ab-test';
import { Accordion } from '../components/accordion';
import { Cards } from '../components/cards';
import { CollageSlider } from '../components/collageSlider';
import { ContentAnchorMenu } from '../components/contentAnchorMenu';
import { EmailFloater } from '../components/emailFloater';
import { GraphQLErrorList } from '../components/graphql-error-list';
import { Greenhouse } from '../components/greenhouse';
import { Hero } from '../components/hero';
import { ImageGridWithColumns } from '../components/imageGridWithColumns';
import { ImageSlider } from '../components/imageSlider';
import { IterablePreferenceCenter } from '../components/iterablePreferenceCenter';
import { LearnMore } from '../components/learnMore';
import { LockingScroll } from '../components/lockingScroll';
import { Marquee } from '../components/marquee';
import { ModuleAbTest } from '../components/module-ab-test';
import { Pricing } from '../components/pricing';
import { ProviderProfileSlider } from '../components/provider-profile-slider';
import { ProviderSearchContainer } from '../components/provider-search-container';
import { RoiCalculator } from '../components/roiCalculator';
import { SEO } from '../components/seo';
import { Stacks } from '../components/stacks';
import { TabbedContent } from '../components/tabbed-content';
import { PreviewContext } from '../context/PreviewContext';
import { pageQuery } from '../graphql/document-queries';
import { MainLayout } from '../layouts/main-layout';
import { camelCase } from '../utils/camel-case';
import * as pageStyles from './page.module.css';

export const query = graphql`
	query PageTemplateQuery($id: String!) {
		sanityPage(id: { eq: $id }) {
			...PageInfo
		}
		sanitySiteSettings(_id: { regex: "/(drafts.|)siteSettings/" }) {
			title
			openGraph {
				title
				description
				image {
					...SanityImage
					asset {
						url
					}
				}
			}
		}
	}
`;

const Page = ({ data, errors }) => {
	// hooks
	const flags = useFlags();
	const { search } = useLocation();

	const [abPageLoaded, setAbPageLoaded] = useState(false);

	const enableCanonicalUrl = data?.sanityPage?.route?.enableCanonicalUrl;
	const canonicalUrl = data?.sanityPage?.route?.canonicalUrl;
	const slug = data?.sanityPage?.route?.slug?.current;

	// get preview data to use in live preview mode in the cms
	const [previewData, sanityPreviewIsLoading] = useLiveQuery(
		null,
		pageQuery,
		{
			slug: slug,
		}
	);

	const {
		setActivePreview,
		setPreviewContextData,
		setPreviewIsLoading,
		setPreviewValidationData,
		setIsNewUnpublishedDoc,
		isNewUnpublishedDoc,
	} = useContext(PreviewContext);

	useEffect(() => {
		setPreviewIsLoading(sanityPreviewIsLoading);
	}, [sanityPreviewIsLoading]);

	// get url params and set preview context for preview mode
	useEffect(() => {
		// Get URL params
		const urlSearchParams = new URLSearchParams(window.location.search);
		const previewModeParameter = urlSearchParams.get('previewMode');
		const previewDatasetParameter = urlSearchParams.get('previewDataset');
		const previewValidationDataParameter =
			urlSearchParams.get('validation');
		const previewIsNewUnpublishedDocParameter =
			urlSearchParams.get('isNewUnpublishedDoc') === 'true';

		if (previewValidationDataParameter) {
			setPreviewValidationData(
				JSON.parse(previewValidationDataParameter)
			);
		}
		if (previewModeParameter) {
			setActivePreview(true);
		}
		if (previewDatasetParameter) {
			setPreviewContextData({ previewDataset: previewDatasetParameter });
		}
		if (previewIsNewUnpublishedDocParameter) {
			setIsNewUnpublishedDoc(previewIsNewUnpublishedDocParameter);
		}
	}, []);

	// handle errors
	if (errors) {
		return (
			<MainLayout>
				<GraphQLErrorList errors={errors} />
			</MainLayout>
		);
	}

	// non published document message
	if (!sanityPreviewIsLoading && isNewUnpublishedDoc) {
		return (
			<div>
				<h3>Preview mode info</h3>
				<h3>
					You have created a new document that isn't yet published.
					Please publish to access preview mode.
				</h3>
			</div>
		);
	}

	// get ab test data
	const abTestKey = camelCase(data?.sanityPage?.abTestLD?.name);
	// abTestValue will return control or variant based on the flagKey
	const abTestValue = flags[abTestKey];
	const abTestActive = data?.sanityPage?.enableAbTestLD;

	useEffect(() => {
		$pageAbTest.setKey('active', abTestActive);
		$pageAbTest.setKey('flagName', abTestKey);
		$pageAbTest.setKey('flagValue', abTestValue);
	}, [abTestActive, abTestKey, abTestValue]);

	const abTestPageData =
		abTestValue &&
		data.sanityPage?.abTestLD?.variations.find((variation) => {
			return variation.testGroup === camelCase(abTestValue);
		})?.page;

	// if there is an abTestKey wait for abTestValue to be set before assigning page data
	// otherwise assign the preview data
	useEffect(() => {
		if (abTestKey && abTestValue) {
			setAbPageLoaded(true);
		}
	}, [abTestKey, abTestValue]);

	// set page data to be either:
	// 1. the ab test variation page data
	// 2. the preview data
	// 3. the regular page data
	const page = abTestActive
		? abTestPageData
		: previewData
			? previewData
			: data.sanityPage;

	const utmParams = data?.sanityPage?.route?.utmParams;

	// assign nav and footer content
	const navContent = page?.navMenu?.[0];
	const footerContent = page?.footerMenu?.[0]?.footerMenu;

	const pageTitle =
		data.sanityPage?.route?.openGraph?.title ||
		data.sanitySiteSettings?.openGraph.title;

	// pull hero image from hero module
	const heroModule = (previewData ? page?.content : page?._rawContent)?.find(
		(module) => module._type === 'hero'
	);
	const heroImage = heroModule?.image?.asset?.url;

	const seoImageUrl =
		data.sanityPage?.route?.openGraph?.image?.asset?.url ||
		heroImage ||
		data.sanitySiteSettings?.openGraph.image.asset.url;
	const description =
		data.sanityPage?.route?.openGraph?.description ||
		data?.site?.description;

	const addSpacer = page?.addSpacer;

	const marketoForm = (previewData ? page?.content : page?._rawContent)?.find(
		(c) => c._type === 'marketoContainer'
	);
	const hasMarketoForm = !!marketoForm;

	let emailFloaterCount = 0;

	const queriedPageContent = page?.content;
	const rawPageContent = page?._rawContent;

	const pageContent = (previewData ? queriedPageContent : rawPageContent)
		?.filter((c) => !c.disabled)
		?.map((c, idx) => {
			let el = null;

			// set data for modules
			// TODO: remove find data when we swap to only using content vs rawContent

			const accordionData = queriedPageContent?.find(
				(module) => module._key === c._key
			);

			const cardsData = queriedPageContent?.find(
				(module) => module._key === c._key
			);

			const contentAnchorMenuData = queriedPageContent?.find(
				(module) => module._key === c._key
			);

			const heroData = queriedPageContent?.find(
				(module) => module._key === c._key
			);

			const imageGridWithColumnsData = queriedPageContent?.find(
				(module) => module._key === c._key
			);

			const lockingScrollData = queriedPageContent?.find(
				(module) => module._key === c._key
			);

			const moduleAbTestData = queriedPageContent?.find(
				(module) => module._key === c._key
			);

			const pricingData = queriedPageContent?.find(
				(module) => module._key === c._key
			);

			const providerProfileSliderData = queriedPageContent?.find(
				(module) => module._key === c._key
			);

			const stacksData = queriedPageContent?.find(
				(module) => module._key === c._key
			);

			const tabsData = queriedPageContent?.find(
				(module) => module._key === c._key
			);

			// get data for email floater
			const excludedUTMList = c?.emailFloater?.excludedUTMList;

			// map over the excludedUTMList and check if the currentUTMs includes any of the excluded UTMs
			const utmCheck = excludedUTMList?.map((excludedUTM) => {
				// if the currentUTMs includes any of the excluded UTMs
				// we dont want to show the email capture
				if (search?.includes(excludedUTM)) {
					return true;
				}
				return false;
			});

			switch (c._type) {
				case 'accordion':
					el = (
						<Accordion
							key={accordionData._key || accordionData._rev}
							{...accordionData}
						/>
					);
					break;
				case 'cards':
					el = (
						<Cards
							key={cardsData._key || cardsData._rev}
							{...cardsData}
						/>
					);
					break;
				case 'collageSlider':
					el = <CollageSlider key={c._key || c._rev} {...c} />;
					break;
				case 'contentAnchorMenuModule':
					el = (
						<ContentAnchorMenu
							key={
								contentAnchorMenuData._key ||
								contentAnchorMenuData._rev
							}
							{...contentAnchorMenuData}
						/>
					);
					break;
				case 'emailFloaterContainer':
					// if utmCheck includes true, we dont want to show the email capture
					if (excludedUTMList && utmCheck?.includes(true)) {
						break;
					}

					if (excludedUTMList && !utmCheck?.includes(true)) {
						emailFloaterCount++;
						el = <EmailFloater key={c._key || c._rev} {...c} />;
					}

					if (!excludedUTMList && emailFloaterCount === 0) {
						el = <EmailFloater key={c._key || c._rev} {...c} />;
						break;
					}

					break;
				case 'greenhouse':
					el = <Greenhouse key={c._key || c._rev} {...c} />;
					break;
				case 'hero':
					el = (
						<Hero
							key={heroData._key || heroData._rev}
							{...heroData}
						/>
					);
					break;
				case 'imageGridWithColumns':
					el = (
						<ImageGridWithColumns
							key={
								imageGridWithColumnsData._key ||
								imageGridWithColumnsData._rev
							}
							{...imageGridWithColumnsData}
						/>
					);
					break;
				case 'iterablePreferenceCenter':
					el = (
						<IterablePreferenceCenter
							key={c._key || c._rev}
							{...c}
						/>
					);
					break;
				case 'learnMore':
					el = <LearnMore key={c._key || c._rev} {...c} />;
					break;
				case 'lockingScrollModule':
					el = (
						<LockingScroll
							key={
								lockingScrollData._key || lockingScrollData._rev
							}
							{...lockingScrollData}
						/>
					);
					break;
				case 'marqueeContainer':
					el = <Marquee key={c._key || c._rev} {...c} />;
					break;
				case 'moduleAbTest':
					el = (
						<ModuleAbTest
							key={moduleAbTestData._key || moduleAbTestData._rev}
							{...moduleAbTestData}
						/>
					);
					break;
				case 'pricingContainer':
					el = (
						<Pricing
							key={pricingData._key || pricingData._rev}
							{...pricingData}
						/>
					);
					break;
				case 'providerProfileSlider':
					el = (
						<ProviderProfileSlider
							key={providerProfileSliderData._key}
							{...providerProfileSliderData}
						/>
					);
					break;
				case 'providerSearchContainer':
					el = (
						<ProviderSearchContainer
							key={c._key || c._rev}
							{...c}
						/>
					);
					break;
				case 'roiCalculator':
					el = <RoiCalculator key={c._key || c._rev} {...c} />;
					break;
				case 'slider':
					el = <ImageSlider key={c._key || c._rev} {...c} />;
					break;
				case 'stacks':
					el = (
						<Stacks
							key={stacksData._key || stacksData._rev}
							{...stacksData}
						/>
					);
					break;
				case 'tabs':
					el = (
						<TabbedContent
							key={tabsData._key || tabsData._rev}
							{...tabsData}
						/>
					);
					break;
				default:
					el = null;
			}

			const addSpacerClass =
				idx === 0 && addSpacer ? pageStyles.spacer : '';

			return (
				<div className={addSpacerClass} id={c._type} key={idx}>
					{el}
				</div>
			);
		});

	if (abTestActive && !abPageLoaded) {
		return null;
	}

	return (
		<MainLayout
			backgroundColor={page?.backgroundColor}
			enableChiliPiper={marketoForm?.enableChiliPiper}
			footerContent={footerContent}
			hasMarketoForm={hasMarketoForm}
			navContent={navContent}
			navTransparency={page?.navTransparency}
			route={slug}
			utmParams={utmParams}
		>
			<SEO
				description={description}
				imageUrl={seoImageUrl}
				keywords={data?.site?.keywords}
				title={pageTitle}
			/>
			{enableCanonicalUrl && canonicalUrl && (
				<Helmet>
					<link rel='canonical' href={canonicalUrl} />
				</Helmet>
			)}
			<div id={slug}>{pageContent}</div>
		</MainLayout>
	);
};

export default Page;
